import { useEffect, useState } from "react";
import { Download, Trophy } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { fmtMoney, authedUrl } from "@/lib/api";
import { Kpi, PageHeader, Money, Empty } from "@/components/Primitives";
import { FilterBar, filterParams } from "@/components/FilterBar";
import { Button } from "@/components/ui/button";
import { monthStartStr, todayStr } from "@/lib/constants";

function MiniTable({ title, rows, testId }) {
  return (
    <div className="panel p-5" data-testid={testId}>
      <div className="eyebrow mb-3">{title}</div>
      {!rows.length ? <div className="text-sm text-slate-600">No data</div> : (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-b border-slate-800/60 last:border-0">
                <td className="py-2">{r.name}<span className="text-xs text-slate-500 ml-2">×{r.count}</span></td>
                <td className="py-2 text-right"><Money value={r.gross} tone="gross" /></td>
                <td className="py-2 text-right"><Money value={r.mco} tone="mco" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function AdminDashboard() {
  const [f, setF] = useState({ preset: "mtd", from: monthStartStr(), to: todayStr() });
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/admin", { params: filterParams(f) }).then((r) => setD(r.data)); }, [f]);

  return (
    <div data-testid="admin-dashboard">
      <PageHeader eyebrow="Management" title="Agency Analytics">
        <Button data-testid="export-csv-btn" variant="outline" className="border-slate-700" onClick={() => window.open(authedUrl("/reports/export", filterParams(f)), "_blank")}>
          <Download className="h-4 w-4 mr-2" />Export CSV
        </Button>
      </PageHeader>
      <FilterBar value={f} onChange={setF} showAgents />
      {!d ? <div className="text-slate-500">Loading…</div> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi testId="kpi-revenue" label="Total Revenue (Gross)" value={fmtMoney(d.totals.revenue)} sub={`${d.totals.bookings} active bookings`} accent="font-mono" />
            <Kpi testId="kpi-supplier-cost" label="Total Supplier Cost" value={fmtMoney(d.totals.supplier_cost)} accent="font-mono text-rose-400/90" />
            <Kpi testId="kpi-mco" label="Net Profit (MCO)" value={fmtMoney(d.totals.mco)} accent="font-mono text-emerald-400" sub={d.totals.revenue ? `${((d.totals.mco / d.totals.revenue) * 100).toFixed(1)}% margin` : ""} />
            <Kpi testId="kpi-charged" label="Charged on Merchants" value={fmtMoney(d.totals.charged_amount)} sub={`${d.totals.pending} pending · ${d.totals.declined} declined/failed`} accent="font-mono" />
          </div>

          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-4 mb-6">
            <div className="panel p-5" data-testid="trend-chart">
              <div className="eyebrow mb-3">Gross vs MCO by day</div>
              <div className="h-64">
                {d.trend.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={d.trend}>
                      <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366f1" stopOpacity={0.5} /><stop offset="100%" stopColor="#6366f1" stopOpacity={0} /></linearGradient>
                        <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.6} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                      </defs>
                      <CartesianGrid stroke="#1e293b" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fill: "#64748b", fontSize: 11 }} width={60} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", fontSize: 12 }} formatter={(v) => fmtMoney(v)} />
                      <Area type="monotone" dataKey="gross" stroke="#6366f1" fill="url(#g1)" name="Gross" />
                      <Area type="monotone" dataKey="mco" stroke="#10b981" fill="url(#g2)" name="MCO" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : <Empty text="No bookings in this range." />}
              </div>
            </div>
            <div className="panel p-5" data-testid="leaderboard">
              <div className="flex items-center gap-2 mb-3"><Trophy className="h-4 w-4 text-amber-400" /><span className="eyebrow">Agent Leaderboard</span></div>
              {!d.leaderboard.length ? <Empty text="No agent activity in range." /> : (
                <table className="w-full text-sm">
                  <thead className="text-[11px] uppercase text-slate-500"><tr><th className="text-left pb-2">#</th><th className="text-left pb-2">Agent</th><th className="text-right pb-2">Bookings</th><th className="text-right pb-2">MCO</th></tr></thead>
                  <tbody>
                    {d.leaderboard.map((a) => (
                      <tr key={a.agent_id} className="border-t border-slate-800/60" data-testid={`leaderboard-row-${a.rank}`}>
                        <td className="py-2 font-mono text-slate-500">{a.rank}</td>
                        <td className="py-2">{a.name}<div className="text-xs text-slate-500">{a.charged_count} charged</div></td>
                        <td className="py-2 text-right font-mono">{a.count}</td>
                        <td className="py-2 text-right"><Money value={a.mco} tone="mco" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <MiniTable title="By product" rows={d.by_product} testId="by-product-table" />
            <MiniTable title="By merchant" rows={d.by_merchant} testId="by-merchant-table" />
          </div>
        </>
      )}
    </div>
  );
}
